function appendIndex(index, key, value) {
    if (key === undefined || key === null || key === '') return
    const normalizedKey = String(key)
    const values = index.get(normalizedKey) || []
    values.push(value)
    index.set(normalizedKey, values)
}

function uniqueIndexValue(index, key) {
    if (key === undefined || key === null || key === '') return null
    const values = index.get(String(key)) || []
    return values.length === 1 ? values[0] : null
}

function referencedTable(reference, tablesById, tablesByName) {
    const tableId = reference?.fk_table_id ?? reference?.fk_table_p_id
    if (tableId !== undefined && tableId !== null && tableId !== '') {
        return uniqueIndexValue(tablesById, tableId)
    }
    return uniqueIndexValue(tablesByName, reference?.fk_table)
}

function relationshipGraph(tableItems) {
    const tablesById = new Map()
    const tablesByName = new Map()
    const itemIndex = new Map()

    tableItems.forEach((item, index) => {
        itemIndex.set(item.data, index)
        appendIndex(tablesById, item.data?.p_id ?? item.data?.id, item.data)
        appendIndex(tablesByName, item.data?.name, item.data)
    })

    const successors = tableItems.map(() => new Set())
    const predecessors = tableItems.map(() => new Set())

    tableItems.forEach((item, sourceIndex) => {
        const references = [
            ...(item.data?.fields || []).filter(field => field.fk && field.fk_table),
            ...(item.data?.logical_relationships || []).filter(relation => relation.fk_table),
        ]

        for (const reference of references) {
            const target = referencedTable(reference, tablesById, tablesByName)
            const targetIndex = itemIndex.get(target)
            if (targetIndex === undefined || targetIndex === sourceIndex) continue

            // Referenced tables are placed before the tables that depend on them.
            successors[targetIndex].add(sourceIndex)
            predecessors[sourceIndex].add(targetIndex)
        }
    })

    return { successors, predecessors }
}

function stronglyConnectedComponents(successors) {
    const discovery = Array(successors.length).fill(-1)
    const lowLink = Array(successors.length).fill(-1)
    const stack = []
    const onStack = new Set()
    const components = []
    let nextDiscovery = 0

    function visit(node) {
        discovery[node] = nextDiscovery
        lowLink[node] = nextDiscovery
        nextDiscovery++
        stack.push(node)
        onStack.add(node)

        for (const successor of successors[node]) {
            if (discovery[successor] === -1) {
                visit(successor)
                lowLink[node] = Math.min(lowLink[node], lowLink[successor])
            } else if (onStack.has(successor)) {
                lowLink[node] = Math.min(lowLink[node], discovery[successor])
            }
        }

        if (lowLink[node] !== discovery[node]) return

        const component = []
        let member
        do {
            member = stack.pop()
            onStack.delete(member)
            component.push(member)
        } while (member !== node)
        component.sort((first, second) => first - second)
        components.push(component)
    }

    successors.forEach((_, node) => {
        if (discovery[node] === -1) visit(node)
    })

    return components
}

function componentRanks(successors, components) {
    const componentForNode = new Map()
    components.forEach((component, componentIndex) => {
        component.forEach(node => componentForNode.set(node, componentIndex))
    })

    const componentSuccessors = components.map(() => new Set())
    const indegree = components.map(() => 0)
    successors.forEach((nodeSuccessors, node) => {
        const sourceComponent = componentForNode.get(node)
        for (const successor of nodeSuccessors) {
            const targetComponent = componentForNode.get(successor)
            if (sourceComponent === targetComponent || componentSuccessors[sourceComponent].has(targetComponent)) continue
            componentSuccessors[sourceComponent].add(targetComponent)
            indegree[targetComponent]++
        }
    })

    const ranks = components.map(() => 0)
    const queue = indegree
        .map((degree, component) => ({ degree, component }))
        .filter(item => item.degree === 0)
        .map(item => item.component)

    while (queue.length) {
        const component = queue.shift()
        for (const successor of componentSuccessors[component]) {
            ranks[successor] = Math.max(ranks[successor], ranks[component] + 1)
            indegree[successor]--
            if (indegree[successor] === 0) queue.push(successor)
        }
    }

    return { componentForNode, ranks }
}

function averageNeighborPosition(node, neighbors, positions) {
    const neighborPositions = [...neighbors[node]]
        .map(neighbor => positions.get(neighbor))
        .filter(position => position !== undefined)
    if (!neighborPositions.length) return null
    return neighborPositions.reduce((sum, position) => sum + position, 0) / neighborPositions.length
}

function reduceCrossings(levels, predecessors, successors) {
    const positions = new Map()
    const refreshPositions = () => {
        levels.forEach(level => level.forEach((node, index) => positions.set(node, index)))
    }
    const sortLevel = (level, neighbors) => {
        level.sort((first, second) => {
            const firstCenter = averageNeighborPosition(first, neighbors, positions)
            const secondCenter = averageNeighborPosition(second, neighbors, positions)
            if (firstCenter === null && secondCenter === null) return first - second
            if (firstCenter === null) return 1
            if (secondCenter === null) return -1
            return firstCenter - secondCenter || first - second
        })
    }

    refreshPositions()
    for (let iteration = 0; iteration < 4; iteration++) {
        for (let level = 1; level < levels.length; level++) {
            sortLevel(levels[level], predecessors)
            refreshPositions()
        }
        for (let level = levels.length - 2; level >= 0; level--) {
            sortLevel(levels[level], successors)
            refreshPositions()
        }
    }
}

function weaklyConnectedComponents(successors, predecessors) {
    const visited = new Set()
    const components = []

    successors.forEach((_, start) => {
        if (visited.has(start)) return

        const component = []
        const stack = [start]
        visited.add(start)

        while (stack.length) {
            const node = stack.pop()
            component.push(node)

            for (const neighbor of [...successors[node], ...predecessors[node]]) {
                if (visited.has(neighbor)) continue
                visited.add(neighbor)
                stack.push(neighbor)
            }
        }

        component.sort((first, second) => first - second)
        components.push(component)
    })

    return components
}

function rankedGraph(tableItems) {
    const { successors, predecessors } = relationshipGraph(tableItems)
    const components = stronglyConnectedComponents(successors)
    const { componentForNode, ranks: componentRank } = componentRanks(successors, components)
    const ranks = tableItems.map((_, node) => componentRank[componentForNode.get(node)])

    return { successors, predecessors, ranks }
}

function levelsForNodes(nodes, ranks, predecessors, successors) {
    const firstRank = Math.min(...nodes.map(node => ranks[node]))
    const levels = []

    nodes.forEach(node => {
        const rank = ranks[node] - firstRank
        if (!levels[rank]) levels[rank] = []
        levels[rank].push(node)
    })

    const populatedLevels = levels.filter(Boolean)
    reduceCrossings(populatedLevels, predecessors, successors)
    return populatedLevels
}

function layoutComponent(tableItems, levels, horizontalGap, verticalGap) {
    const columnWidths = levels.map(level =>
        Math.max(...level.map(node => tableItems[node].width), 0)
    )
    const columnHeights = levels.map(level =>
        level.reduce((height, node) => height + tableItems[node].height, 0)
        + Math.max(0, level.length - 1) * verticalGap
    )
    const height = Math.max(...columnHeights, 0)
    const positions = new Map()
    let currentX = 0

    levels.forEach((level, levelIndex) => {
        let currentY = (height - columnHeights[levelIndex]) / 2

        level.forEach(node => {
            positions.set(node, { left: currentX, top: currentY })
            currentY += tableItems[node].height + verticalGap
        })

        currentX += columnWidths[levelIndex] + horizontalGap
    })

    return {
        positions,
        width: Math.max(0, currentX - horizontalGap),
        height,
    }
}

export function relationshipLevels(tableItems) {
    if (!tableItems.length) return []

    const { successors, predecessors, ranks } = rankedGraph(tableItems)
    const levels = []

    tableItems.forEach((_, node) => {
        const rank = ranks[node]
        if (!levels[rank]) levels[rank] = []
        levels[rank].push(node)
    })

    reduceCrossings(levels, predecessors, successors)
    return levels
}

export function calculateRelationshipLayout(tableItems, noteItems = [], options = {}) {
    const {
        startX = 40,
        startY = 40,
        horizontalGap = 120,
        verticalGap = 50,
        noteGap = 40,
        componentGap = 60,
        maxWidth = 1200,
    } = options
    const positions = new Map()
    const availableWidth = Math.max(maxWidth, 320)
    let currentX = startX
    let currentY = startY
    let rowBottom = startY
    let diagramBottom = startY

    if (tableItems.length) {
        const { successors, predecessors, ranks } = rankedGraph(tableItems)
        const components = weaklyConnectedComponents(successors, predecessors)

        components.forEach(nodes => {
            const levels = levelsForNodes(nodes, ranks, predecessors, successors)
            const component = layoutComponent(tableItems, levels, horizontalGap, verticalGap)

            if (
                currentX > startX
                && currentX + component.width > startX + availableWidth
            ) {
                currentX = startX
                currentY = rowBottom + componentGap
            }

            component.positions.forEach((position, node) => {
                positions.set(tableItems[node].data, {
                    left: currentX + position.left,
                    top: currentY + position.top,
                })
            })

            rowBottom = Math.max(rowBottom, currentY + component.height)
            diagramBottom = Math.max(diagramBottom, currentY + component.height)
            currentX += component.width + componentGap
        })
    }

    if (noteItems.length) {
        const notesTop = tableItems.length ? diagramBottom + verticalGap * 2 : startY
        let noteX = startX
        let noteY = notesTop
        let nextRowY = noteY

        for (const note of noteItems) {
            if (noteX > startX && noteX + note.width > startX + availableWidth) {
                noteX = startX
                noteY = nextRowY
            }
            positions.set(note.data, { left: noteX, top: noteY })
            noteX += note.width + noteGap
            nextRowY = Math.max(nextRowY, noteY + note.height + noteGap)
        }
    }

    return positions
}
